// Shared visual config for the reactour TourProvider, kept with the rest of the
// tour code (rather than inline in App.jsx). Passed as <TourProvider styles>.

// How dark the spotlight overlay is. reactour's default is 0.7; lower it for a
// lighter dim (0 = no dim, 1 = solid black). Steps that hide the mask entirely
// (welcome/finish slides, click-through steps) set their own maskWrapper opacity
// and so override this — it only affects the normal dimmed steps.
export const TOUR_MASK_OPACITY = 0.4;

// Ring drawn around the highlighted (un-dimmed) element so it's obvious what to
// click. Tweak colour / thickness here.
export const SPOTLIGHT_BORDER = { color: '#1e88e5', width: 3 };

type StyleFn = (base: Record<string, unknown>) => Record<string, unknown>;

// padding (20) is matched by the tail offset in TourStepContent;
// overflow:visible lets the speech-bubble tail stick out of the popover.
export const tourStyles: {
	popover: StyleFn;
	maskWrapper: StyleFn;
	clickArea: StyleFn;
	highlightedArea: StyleFn;
} = {
	popover: (base) => ({
		...base,
		borderRadius: 8,
		padding: 20,
		background: '#eeeeee',
		overflow: 'visible',
		border: '2px solid #000',
		boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
	}),
	maskWrapper: (base) => ({ ...base, opacity: TOUR_MASK_OPACITY }),
	// The invisible overlay that swallows clicks on the dimmed area (everything
	// but the spotlight hole). It already blocks — give it a "no" cursor so it's
	// obvious that area isn't clickable. Click-through steps override clickArea
	// (pointerEvents:none), so they don't inherit this.
	clickArea: (base) => ({ ...base, cursor: 'not-allowed' }),
	// The rect over the highlighted element. reactour hides it by default; we show
	// it as a click-through ring (pointerEvents:none keeps the target clickable).
	// It lives inside maskWrapper, so mask-hidden steps (opacity 0) hide it too —
	// only normal dimmed steps get the ring. Steps that dim the whole page without
	// a real target (e.g. fullGrayMask) opt out with `highlightedArea: display:none`.
	highlightedArea: (base) => ({
		...base,
		display: 'block',
		pointerEvents: 'none',
		fill: 'transparent',
		stroke: SPOTLIGHT_BORDER.color,
		strokeWidth: SPOTLIGHT_BORDER.width,
	}),
};
